import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Video, Calendar, Users, AlertTriangle, CheckCircle, Clock } from "lucide-react";
import { API_URL } from "@/lib/apiUrl";

export function SectionVideosEnApproche({ deptView }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchVideos();
  }, [deptView]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/crm/videos-en-approche`);
      if (res.status === 200) {
        let videosData = res.data.videos || [];
        
        // Filtrer par département si nécessaire
        if (deptView && deptView !== 'global') {
          videosData = videosData.filter(v => v.departement === deptView);
        }
        
        setVideos(videosData);
      }
    } catch (error) {
      console.error("Erreur chargement vidéos:", error);
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyBadge = (joursRestants) => {
    if (joursRestants === null || joursRestants === undefined) {
      return <Badge variant="outline" className="text-xs">Date non définie</Badge>;
    }
    if (joursRestants < 0) {
      return <Badge variant="secondary" className="text-xs">Il y a {Math.abs(joursRestants)} jour{Math.abs(joursRestants) > 1 ? 's' : ''}</Badge>;
    }
    if (joursRestants === 0) {
      return <Badge className="bg-red-600 text-xs">🔥 Aujourd'hui</Badge>;
    }
    if (joursRestants === 1) {
      return <Badge className="bg-orange-600 text-xs">🔴 Demain</Badge>;
    }
    if (joursRestants <= 3) {
      return <Badge className="bg-orange-600 text-xs">🔴 Dans {joursRestants} jours</Badge>;
    }
    if (joursRestants <= 7) {
      return <Badge className="bg-yellow-600 text-xs">🟡 Dans {joursRestants} jours</Badge>;
    }
    return <Badge variant="secondary" className="text-xs">🟢 Dans {joursRestants} jours</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Non définie";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return "Date invalide";
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-purple-600" />
            Création vidéo en approche
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-2 text-sm">Chargement...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (videos.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-purple-600" />
            Création vidéo en approche
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500">
            <Video className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="font-medium">Aucune création vidéo prévue</p>
            <p className="text-sm mt-1">
              {deptView === 'global' ? "Tous départements confondus" : `Pour le département ${deptView}`}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-purple-200">
      <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-purple-600" />
            Création vidéo en approche
            <Badge variant="secondary">{videos.length} projet{videos.length > 1 ? 's' : ''}</Badge>
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchVideos}
            className="text-xs"
          >
            Actualiser
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="space-y-3">
          {videos.map((video, idx) => (
            <div
              key={video.id || idx}
              className="border rounded-lg p-3 hover:bg-slate-50 cursor-pointer transition-colors"
              onClick={() => navigate(`/crm/projets/${video.id}?tab=photos`)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* Client et projet */}
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-slate-900 truncate">
                      {video.client_nom}
                    </p>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {video.departement}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 truncate mb-2">
                    {video.nom_dossier}
                  </p>

                  {/* Infos */}
                  <div className="flex flex-wrap gap-3 text-xs text-slate-600">
                    {/* Date prévue */}
                    {video.video_date_prevue && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{formatDate(video.video_date_prevue)}</span>
                      </div>
                    )}

                    {/* Durée estimée */}
                    {video.video_duree_estimee && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{video.video_duree_estimee}s</span>
                      </div>
                    )}

                    {/* Checklist */}
                    {video.checklist_progression && (
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5" />
                        <span>{video.checklist_progression}</span>
                      </div>
                    )}
                  </div>

                  {/* Employés assignés */}
                  {video.video_employes_noms && video.video_employes_noms.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-2">
                      <Users className="w-3.5 h-3.5 text-purple-600" />
                      <div className="flex flex-wrap gap-1">
                        {video.video_employes_noms.map((nom, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-purple-50 border-purple-200 text-purple-700">
                            {nom}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  {video.video_tags && video.video_tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {video.video_tags.slice(0, 3).map((tag, tagIdx) => (
                        <Badge key={tagIdx} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      {video.video_tags.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{video.video_tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>

                {/* Badge urgence */}
                <div className="shrink-0">
                  {getUrgencyBadge(video.jours_restants)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default SectionVideosEnApproche;
